import React from 'react';

// Returns true if the error is a failed lazy-chunk load (stale client after a
// deploy, or a transient edge/cache miss). The message/name varies by browser.
function isChunkLoadError(error) {
  if (!error) return false;
  const name = error.name || '';
  const msg = error.message || '';
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /Unexpected token '<'/.test(msg)
  );
}

const RELOAD_TS_KEY = 'chunk-reload-ts';
// If we already hard-reloaded within this window and STILL hit a chunk error, the
// reload didn't help (e.g. a genuinely poisoned edge cache) — stop, don't loop.
// After the window passes, a future deploy is free to trigger a fresh recovery.
const RELOAD_COOLDOWN_MS = 15000;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    if (isChunkLoadError(error)) {
      let last = 0;
      try {
        last = parseInt(sessionStorage.getItem(RELOAD_TS_KEY) || '0', 10);
      } catch (e) {
        /* sessionStorage unavailable */
      }
      const now = Date.now();
      if (now - last > RELOAD_COOLDOWN_MS) {
        // Force one hard reload to fetch the current index.html + chunk hashes.
        try {
          sessionStorage.setItem(RELOAD_TS_KEY, String(now));
        } catch (e) {
          /* ignore */
        }
        window.location.reload();
      }
      return { hasError: true };
    }

    if (error.message && error.message.includes('ResizeObserver loop completed')) {
      return { hasError: true }; // hide error
    }
    return null;
  }

  componentDidCatch(error, info) {
    if (!isChunkLoadError(error)) {
      console.error('Error caught by boundary:', error);
    }
  }

  render() {
    if (this.state.hasError) {
      return null; // or fallback UI
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
